<!--
📊 Analytics Tracker Component
Día 5: Sistema de Routing y Navegación - Analytics Integration
-->
<template>
  <div class="analytics-tracker">
    <!-- Hidden analytics component - no visual -->
  </div>
</template>

<script setup lang="ts">
import { watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useNavigationStore } from '@/stores/navigation'

const route = useRoute()
const navigationStore = useNavigationStore()

// 📊 Analytics Event Interface
interface AnalyticsEvent {
  category: string
  action: string
  label?: string
  value?: number
  custom_parameters?: Record<string, any>
}

// 🎯 Track Page Views
const trackPageView = (path: string, title: string) => {
  const event: AnalyticsEvent = {
    category: 'navigation',
    action: 'page_view',
    label: path,
    custom_parameters: {
      page_title: title,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    }
  }
  
  sendAnalytics(event)
}

// 🔄 Track User Interactions
const trackUserInteraction = (action: string, element: string, value?: number) => {
  const event: AnalyticsEvent = {
    category: 'user_interaction',
    action,
    label: element,
    value,
    custom_parameters: {
      timestamp: new Date().toISOString(),
      page: route.path
    }
  }
  
  sendAnalytics(event)
}

// ⏱️ Track Performance Metrics
const trackPerformance = (metric: string, value: number, unit: string = 'ms') => {
  const event: AnalyticsEvent = {
    category: 'performance',
    action: metric,
    value,
    custom_parameters: {
      unit,
      page: route.path,
      timestamp: new Date().toISOString()
    }
  }
  
  sendAnalytics(event)
}

// 🚨 Track Errors
const trackError = (error: Error, context?: string) => {
  const event: AnalyticsEvent = {
    category: 'error',
    action: 'javascript_error',
    label: error.message,
    custom_parameters: {
      stack: error.stack,
      context,
      page: route.path,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent
    }
  }
  
  sendAnalytics(event)
}

// 📤 Send Analytics to Backend/Service
const sendAnalytics = async (event: AnalyticsEvent) => {
  try {
    // Console logging for development
    console.log('📈 Analytics Event:', event)
    
    // In production, this would send to your analytics service
    // Examples: Google Analytics, Mixpanel, Custom Analytics API
    
    // Example implementation:
    /*
    await fetch('/api/analytics', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event)
    })
    */
    
    // Store in navigation store for dashboard use
    navigationStore.addAnalyticsEvent(event)
    
  } catch (error) {
    console.warn('Failed to send analytics:', error)
  }
}

// 🎯 Route Change Tracking
watch(
  () => route.path,
  (newPath, oldPath) => {
    if (oldPath) {
      // Track navigation
      trackPageView(newPath, route.meta.title as string || 'Unknown Page')
      
      // Track navigation timing
      const navigationTime = performance.now()
      trackPerformance('navigation_time', navigationTime)
    }
  },
  { immediate: true }
)

// 🎮 Global Click Tracking
onMounted(() => {
  // Track clicks on important elements
  const trackClicks = (event: MouseEvent) => {
    const target = event.target as HTMLElement
    
    if (target) {
      // Track button clicks
      if (target.tagName === 'BUTTON' || target.classList.contains('v-btn')) {
        const buttonText = target.textContent?.trim() || 'Unknown Button'
        trackUserInteraction('click', `button:${buttonText}`)
      }
      
      // Track navigation links
      if (target.tagName === 'A' || target.closest('.v-list-item')) {
        const linkText = target.textContent?.trim() || target.getAttribute('href') || 'Navigation Link'
        trackUserInteraction('click', `navigation:${linkText}`)
      }
      
      // Track form submissions
      if (target.tagName === 'FORM' || target.closest('form')) {
        trackUserInteraction('submit', 'form')
      }
    }
  }
  
  document.addEventListener('click', trackClicks)
  
  // Track page load performance
  window.addEventListener('load', () => {
    const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart
    trackPerformance('page_load_time', loadTime)
  })
  
  // Track errors
  window.addEventListener('error', (event) => {
    trackError(new Error(event.message), `${event.filename}:${event.lineno}:${event.colno}`)
  })
  
  // Track unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    trackError(new Error(`Unhandled Promise Rejection: ${event.reason}`), 'promise_rejection')
  })
  
  // Cleanup
  return () => {
    document.removeEventListener('click', trackClicks)
  }
})

// 📊 Expose tracking functions for manual use
defineExpose({
  trackPageView,
  trackUserInteraction,
  trackPerformance,
  trackError
})
</script>

<style lang="scss" scoped>
.analytics-tracker {
  display: none; // Hidden component for analytics only
}
</style>
